import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

type LabelAdapter = (categoryName: string) => string;

// Minimal structural shape of the amCharts column "hit" event consumed by
// call sites (they read e.target.dataItem.dataContext.<field>). dataContext is
// the chart's row datum, whose shape varies per widget, hence Record.
export interface SeriesHitEvent {
  target: {
    dataItem: {
      dataContext?: Record<string, unknown>;
    };
  };
}

export type SeriesClickHandler = (
  event: SeriesHitEvent,
  // Optional: the PieChart call site invokes the handler with a single arg,
  // while createSeries (below) always passes both. The hook implementation
  // defaults this to null, so an omitted second arg is tolerated at runtime.
  valueField?: string | null
) => void;

export const createCategoryAxis = (
  categoryAxis: am4charts.CategoryAxis,
  categoryTitle: string,
  categoryField: string,
  isVertical: boolean,
  labelAdapter: LabelAdapter | null = null,
  displayTooltip = true
): void => {
  categoryAxis.dataFields.category = categoryField;
  categoryAxis.title.text = categoryTitle;
  categoryAxis.renderer.labels.template.fontSize = 12;
  categoryAxis.title.fontSize = 12;
  categoryAxis.title.fontWeight = 500 as unknown as am4core.FontWeight;
  categoryAxis.renderer.minGridDistance = 8;
  categoryAxis.title.align = "center";
  categoryAxis.title.valign = "top";
  categoryAxis.renderer.grid.template.location = 0;
  categoryAxis.renderer.grid.template.strokeOpacity = 0;
  categoryAxis.renderer.labels.template.truncate = true;

  if (displayTooltip) {
    categoryAxis.renderer.labels.template.tooltipText = "{category}";
    categoryAxis.renderer.labels.template.adapter.add(
      "tooltipText",
      (text, target) => {
        const categoryName = (target.dataItem as am4charts.CategoryAxisDataItem)
          .category;
        return !!labelAdapter ? labelAdapter(categoryName) : categoryName;
      }
    );
  }

  categoryAxis.renderer.labels.template.adapter.add("text", (text, target) => {
    const categoryName = (target.dataItem as am4charts.CategoryAxisDataItem)
      .category;
    return !!labelAdapter ? labelAdapter(categoryName) : categoryName;
  });

  if (isVertical) {
    categoryAxis.events.on("sizechanged", function (ev) {
      let axis = ev.target;
      let cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);
      axis.renderer.labels.template.maxWidth = cellWidth;
    });
    categoryAxis.renderer.labels.template.tooltip!.rotation = -90;
    categoryAxis.renderer.labels.template.tooltip!.label.maxWidth = 200;
  } else {
    categoryAxis.title.dy = -19;
    categoryAxis.title.rotation = 0;
    categoryAxis.paddingRight = 20;
    categoryAxis.paddingLeft = 10;
    categoryAxis.layout = "absolute";
    categoryAxis.renderer.labels.template.maxWidth = 120;
    categoryAxis.renderer.labels.template.tooltip!.label.maxWidth = 400;
  }
};

export const createValueAxis = (
  valueAxis: am4charts.ValueAxis,
  valueTitle: string
): void => {
  valueAxis.title.text = valueTitle;
  valueAxis.title.fontSize = 12;
  valueAxis.title.fontWeight = 500 as unknown as am4core.FontWeight;
  valueAxis.renderer.labels.template.fontSize = 12;
};

export const createSeries = (
  chart: am4charts.XYChart,
  isVertical: boolean,
  categories: unknown,
  onClick: SeriesClickHandler | null,
  valueField: string,
  categoryField: string,
  seriesName: string | null = null,
  isStacked = false,
  color: string | null = null,
  isExported: boolean | undefined
): void => {
  const series = chart.series.push(new am4charts.ColumnSeries());
  series.tooltip!.autoTextColor = false;
  series.columns.template.strokeOpacity = 0;

  if (isVertical) {
    series.dataFields.valueY = valueField;
    series.dataFields.categoryX = categoryField;
    series.columns.template.tooltipText = isStacked
      ? "[bold]{name}[/]\n[font-size:14px]{valueY}"
      : "[bold]{valueY}[/]";
    series.columns.template.maxWidth = 100;
  } else {
    series.dataFields.valueX = valueField;
    series.dataFields.categoryY = categoryField;
    series.columns.template.tooltipText = isStacked
      ? "[bold]{name}[/]\n[font-size:14px]{valueX}"
      : "[bold]{valueX}[/]";
    series.columns.template.height = 12;
  }

  if (isStacked) {
    series.name = seriesName as string;
    series.stacked = true;
    series.columns.template.strokeOpacity = 1;
    // amCharts coerces this string to a numeric stroke width at runtime; preserved as-is.
    series.columns.template.strokeWidth = "1px" as unknown as number;

    series.columns.template.height = am4core.percent(60);
    series.columns.template.maxHeight = 80;

    // add legend if stacked
    chart.legend.useDefaultMarker = true;
    let markerTemplate = chart.legend.markers.template;
    markerTemplate.width = 12;
    markerTemplate.height = 12;
    markerTemplate.strokeOpacity = 0;
    series.legendSettings.labelText = `[bold]{name}: [/]${
      isVertical ? "{valueY.close}" : "{valueX.close}"
    }`;
  }

  if (isExported && !isStacked) {
    const labelBullet = series.bullets.push(new am4charts.LabelBullet());
    labelBullet.fontSize = 14;
    labelBullet.fontWeight = "500";
    labelBullet.label.text = isVertical ? "{valueY}" : "{valueX}";
    labelBullet.label.truncate = false;
    labelBullet.label.hideOversized = false;
    chart.maskBullets = false;

    if (isVertical) {
      labelBullet.locationY = 0;
      labelBullet.label.adapter.add("verticalCenter", (center, target) => {
        return (target.dataItem as am4charts.XYSeriesDataItem)?.valueY < 0
          ? "top"
          : "bottom";
      });
      labelBullet.label.adapter.add("dy", (dy, target) => {
        return (target.dataItem as am4charts.XYSeriesDataItem)?.valueY < 0
          ? 10
          : -10;
      });
    } else {
      labelBullet.locationX = 0;
      labelBullet.label.adapter.add("horizontalCenter", (center, target) => {
        return (target.dataItem as am4charts.XYSeriesDataItem)?.valueX < 0
          ? "right"
          : "left";
      });
      labelBullet.label.adapter.add("dx", (dx, target) => {
        return (target.dataItem as am4charts.XYSeriesDataItem)?.valueX < 0
          ? -10
          : 10;
      });
    }
  }

  // colors handling
  if (!isStacked && !!categories) {
    series.columns.template.propertyFields.fill = "fill";
  } else if (isStacked && !!color) {
    series.fill = am4core.color(color);
  } else if (!isStacked) {
    series.columns.template.adapter.add("fill", function (fill, target) {
      return chart.colors.getIndex(target.dataItem!.index);
    });
  }

  // corner radius
  series.columns.template.events.on("validated", function (event) {
    const column = event.target;
    const borderRadius = isStacked ? 3 : 10;
    if (isVertical) {
      if ((column.dataItem as am4charts.ColumnSeriesDataItem).valueY < 0) {
        column.column.cornerRadiusBottomLeft = borderRadius;
        column.column.cornerRadiusBottomRight = borderRadius;
        column.column.cornerRadiusTopLeft = 0;
        column.column.cornerRadiusTopRight = 0;
      } else {
        column.column.cornerRadiusTopLeft = borderRadius;
        column.column.cornerRadiusTopRight = borderRadius;
        column.column.cornerRadiusBottomLeft = 0;
        column.column.cornerRadiusBottomRight = 0;
      }
    } else {
      if ((column.dataItem as am4charts.ColumnSeriesDataItem).valueX < 0) {
        column.column.cornerRadiusBottomLeft = borderRadius;
        column.column.cornerRadiusTopLeft = borderRadius;
        column.column.cornerRadiusTopRight = 0;
        column.column.cornerRadiusBottomRight = 0;
      } else {
        column.column.cornerRadiusTopRight = borderRadius;
        column.column.cornerRadiusBottomRight = borderRadius;
        column.column.cornerRadiusBottomLeft = 0;
        column.column.cornerRadiusTopLeft = 0;
      }
    }
  });

  const hs = series.columns.template.states.create("hover");
  hs.properties.fillOpacity = 1;

  if (!!onClick) {
    series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    series.columns.template.events.on("hit", (e) => {
      // amCharts' hit-event type is wider than the structural shape consumers
      // read; cast to the consumed SeriesHitEvent shape.
      onClick(e as unknown as SeriesHitEvent, isStacked ? valueField : null);
    });
  }
};
